<template>
  <div>
    <div class="flex flex-wrap -mx-2">

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-input
          label="Sueldo base"
          required
          type="number"
          v-model="form.baseSalary"
          placeholder="Ingresar sueldo base"
          :disabled="baseSalaryLocked"
          :helper="baseSalaryLocked ? 'Sueldo tomado de la adenda' : ''"
          :error="v.baseSalary.$error
            ? (!v.baseSalary.required ? 'Sueldo base es requerido'
              : !v.baseSalary.numeric  ? 'El campo debe ser numérico' : '')
            : ''"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Moneda"
          v-model="form.salaryCurrency"
          :items="currencyList"
          defaultOption="Elegir moneda"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Régimen laboral"
          required
          v-model="form.laborRegime"
          :items="laborRegimeList"
          defaultOption="Elegir régimen"
          :error="v.laborRegime.$error && !v.laborRegime.required ? 'Régimen laboral es requerido' : ''"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Categoría ocupacional"
          v-model="form.occupationalCategory"
          :items="occupationalCategoryList"
          defaultOption="Elegir categoría"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Tipo de trabajador"
          v-model="form.workerType"
          :items="workerTypeList"
          defaultOption="Elegir tipo"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Jornada laboral"
          v-model="form.workSchedule"
          :items="workScheduleList"
          defaultOption="Elegir jornada"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-input
          label="Horas semanales"
          type="number"
          v-model="form.weeklyHours"
          placeholder="48"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Situación especial"
          v-model="form.specialSituation"
          :items="specialSituationList"
          defaultOption="Elegir situación"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-input
          label="Email corporativo"
          type="email"
          v-model.trim="form.corporateEmail"
          placeholder="Ingresar email corporativo"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-input
          label="Celular corporativo"
          :value="form.corporatePhone"
          placeholder="Ingresar celular corporativo"
          maxlength="20"
          @input="onCorporatePhoneInput"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <div class="flex items-center mt-4 pt-2">
          <label class="wz-form-label mb-0 mr-3">Asignación familiar</label>
          <wz-checkbox
            v-model="form.hasFamilyAllowance"
            name="hasFamilyAllowance"
            switch
            size="lg"
            :disabled="true"
          />
        </div>
        <small class="text-muted d-block mt-1">
          <i class="ri-information-line"></i>
          Gestionado automáticamente según hijos calificados registrados.
        </small>
      </div>

      <!-- Resumen de hijos para asignación familiar -->
      <div class="w-full px-2" v-if="form.hasFamilyAllowance">

        <template v-if="dependentSummary.qualifiedChildren > 0">
          <div class="overflow-x-auto mb-2">
            <table class="wz-table w-full px-2">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Documento</th>
                  <th>Edad</th>
                  <th>Estado AF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="child in dependentSummary.children" :key="child.id">
                  <td>{{ child.fullName }}</td>
                  <td>{{ getDocTypeLabel(child.documentType) }} {{ child.documentNumber }}</td>
                  <td>{{ child.age }} años</td>
                  <td>
                    <wz-badge :variant="getFamilyStatusColor(child.familyAllowanceStatus)">
                      {{ getFamilyStatusLabel(child.familyAllowanceStatus) }}
                    </wz-badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <wz-alert variant="success" icon="ri-check-line" class="mb-3">
            {{ dependentSummary.qualifiedChildren }} hijo(s) calificado(s) para asignación familiar<template v-if="familyAllowanceLabel"> ({{ familyAllowanceLabel }})</template>
          </wz-alert>
        </template>

        <template v-else-if="dependentSummary.totalChildren > 0">
          <div class="overflow-x-auto mb-2">
            <table class="wz-table w-full px-2">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Documento</th>
                  <th>Edad</th>
                  <th>Estado AF</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="child in dependentSummary.children" :key="child.id">
                  <td>{{ child.fullName }}</td>
                  <td>{{ getDocTypeLabel(child.documentType) }} {{ child.documentNumber }}</td>
                  <td>{{ child.age }} años</td>
                  <td>
                    <wz-badge :variant="getFamilyStatusColor(child.familyAllowanceStatus)">
                      {{ getFamilyStatusLabel(child.familyAllowanceStatus) }}
                    </wz-badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <wz-alert variant="warning" icon="ri-alert-line" class="mb-3">
            Ninguno de los hijos registrados cumple las condiciones para asignación familiar.
          </wz-alert>
        </template>

        <template v-else>
          <wz-alert variant="light" icon="ri-information-line" class="mb-3">
            No hay hijos registrados para este colaborador.
            <router-link
              v-if="employeeId"
              :to="{ name: 'employee-detail', params: { id: String(employeeId) }, query: { tab: 'familia' } }"
              class="ml-1"
            >Ir a Ficha del colaborador &rsaquo; Familia</router-link>
          </wz-alert>
        </template>

      </div>

    </div>

    <!-- Tipos de planilla -->
    <hr class="my-3" />
    <h6 class="text-muted mb-3">Tipos de planilla <span class="text-danger">*</span></h6>
    <div class="flex flex-wrap -mx-2" v-if="payrollTypes.length > 0">
      <div class="w-full md:w-1/2 mb-3 px-2" v-for="pt in payrollTypes" :key="pt.id">
        <div class="flex items-center">
          <wz-checkbox
            :value="pt.id"
            v-model="form.payrollTypeIds"
            class="mr-2"
            @change="onTypeToggle(pt)"
          />
          <span>{{ pt.name }}</span>
          <wz-badge class="ml-2" :variant="getFrequencyBadge(pt.frequency)" style="font-size: 0.7rem;">
            {{ getFrequencyLabel(pt.frequency) }}
          </wz-badge>
          <wz-badge class="ml-1" :variant="pt.taxCategory === 'FIFTH' ? 'info-solid' : 'secondary-solid'" style="font-size: 0.7rem;">
            {{ pt.taxCategory === 'FIFTH' ? '5ta' : '4ta' }}
          </wz-badge>
          <wz-badge v-if="pt.calculationMode === 'FIXED'" class="ml-1" variant="warning-solid" style="font-size: 0.7rem;">
            Monto fijo
          </wz-badge>
        </div>
        <div v-if="isSelected(pt.id) && pt.calculationMode === 'FIXED'" class="mt-2 ml-4" style="max-width: 220px;">
          <wz-input
            label="Monto fijo (S/)"
            type="number"
            min="0"
            step="0.01"
            :value="form.fixedAmounts[pt.id]"
            @input="setFixedAmount(pt.id, $event)"
            :error="fixedAmountError(pt.id)"
          />
        </div>
      </div>
    </div>
    <p v-else class="text-muted text-sm">No hay tipos de planilla configurados.</p>
    <p
      v-if="v.payrollTypeIds && v.payrollTypeIds.$error && !v.payrollTypeIds.required"
      class="wz-form-error-msg text-danger text-sm mt-1 mb-0"
    >
      Debe seleccionar al menos un tipo de planilla.
    </p>

  </div>
</template>

<script>
import { Constants } from '@/core/utils'
import familyAllowanceLabelMixin from '@/core/mixins/familyAllowanceLabel'

export default {
  mixins: [familyAllowanceLabelMixin],
  props: {
    form:             { type: Object, required: true },
    v:                { type: Object, required: true },
    dependentSummary: { type: Object, default: () => ({ totalChildren: 0, qualifiedChildren: 0, hasQualifiedChildren: false, children: [] }) },
    employeeId:       { type: Number, default: null },
    payrollTypes:     { type: Array,  default: () => [] },
    // Renovación con adenda de sueldo: el sueldo base se toma de la adenda → input bloqueado.
    baseSalaryLocked: { type: Boolean, default: false },
  },
  data() {
    return {
      currencyList:            Object.values(Constants.SalaryCurrency),
      laborRegimeList:         Object.values(Constants.LaborRegime),
      occupationalCategoryList: Object.values(Constants.OccupationalCategory),
      workerTypeList:          Object.values(Constants.WorkerType),
      workScheduleList:        Object.values(Constants.WorkSchedule),
      specialSituationList:    Object.values(Constants.SpecialSituation),
    }
  },
  methods: {
    onCorporatePhoneInput(val) {
      this.form.corporatePhone = (val || '').replace(/\D/g, '')
    },
    getDocTypeLabel(type) {
      const found = Object.values(Constants.IdentifierType).find(t => String(t.key) === String(type))
      return found ? found.label : type
    },
    getFamilyStatusColor(status) {
      const found = Object.values(Constants.FamilyAllowanceStatus).find(s => s.key === status)
      return found ? found.color : 'dark'
    },
    getFamilyStatusLabel(status) {
      const found = Object.values(Constants.FamilyAllowanceStatus).find(s => s.key === status)
      return found ? found.label : (status || '-')
    },
    getFrequencyLabel(freq) {
      const map = { WEEKLY: 'Semanal', BIWEEKLY: 'Quincenal', MONTHLY: 'Mensual' }
      return map[freq] || freq
    },
    getFrequencyBadge(freq) {
      const map = { WEEKLY: 'warning-solid', BIWEEKLY: 'info-solid', MONTHLY: 'success-solid' }
      return map[freq] || 'dark'
    },
    isSelected(id) {
      return (this.form.payrollTypeIds || []).includes(id)
    },
    setFixedAmount(id, val) {
      this.$set(this.form.fixedAmounts, id, (val === '' || val == null) ? null : Number(val))
    },
    // Al quitar un tipo, limpiar su monto fijo del payload.
    onTypeToggle(pt) {
      if (!this.isSelected(pt.id)) this.$set(this.form.fixedAmounts, pt.id, null)
    },
    fixedAmountError(id) {
      const v = this.form.fixedAmounts ? this.form.fixedAmounts[id] : null
      return (v != null && v !== '' && !(Number(v) > 0)) ? 'Debe ser mayor a 0' : ''
    },
  },
}
</script>

<style lang="stylus" scoped></style>
